<template>
  <div :class="['profile-container', { vertical: props.vertical }]" v-if="!loading">
    <div class="left">
      <div class="detail-section">
        <span class="worker-name">{{ formatWorkerName(profile?.worker_name) }}</span>
        <UserRatingDisplay :rating="profile?.raise_rating_avg || 0" small></UserRatingDisplay>
        <span v-if="profile?.worker_local_authority && profile?.distance_km" class="subheading location"
          >{{ profile?.worker_local_authority }}
          <span v-if="profile?.distance_km !== undefined">{{ ', ' + profile?.distance_km }} km away</span></span
        >
      </div>
      <div class="detail-section">
        <span v-if="matchingRole?.days_in_role || matchingRole?.hours_in_role" class="light-subheading experience-text"
          >{{ getAllShiftsWorkedTimeSpan(matchingRole?.days_in_role, matchingRole?.hours_in_role) }} experience</span
        >
        <!-- TODO: should this include all or matching WS verified experience. How do we indicate what makes this up? -->
        <span v-if="daysInRole || hoursInRole"
          ><span class="stat-bold">{{ getAllShiftsWorkedTimeSpan(daysInRole, hoursInRole) }}</span>
          as
          {{ matchingRole?.internal_role?.role }}</span
        >
        <!-- TODO: should this include matching role WS verified experience. How do we indicate what makes this up? -->
        <span v-if="daysInFunction || hoursInFunction"
          ><span class="stat-bold">{{ getAllShiftsWorkedTimeSpan(daysInFunction, hoursInFunction) }}</span>
          as
          {{ matchingRole?.internal_role?.function }}</span
        >
        <!-- TODO: should this include matching role WS verified experience. How do we indicate what makes this up? -->
        <span>
          <span class="stat-bold">{{ totalShifts }} shifts</span> completed</span
        >
      </div>
      <b-button v-if="props.claim" class="pick-button" variant="primary" @click="emit('claim-selected')">
        <img :src="themeImage('check-circle-white.svg')" alt="Raise" />
        Pick {{ profile?.worker_name?.split(' ')[0] }} for this shift</b-button
      >
    </div>
    <div class="right">
      <!-- VERIFIED -->
      <div class="section" v-if="verifiedExperience?.length > 0">
        <span class="heading">Verified experience</span>
        <div v-for="(experience, index) in verifiedExperience" class="role" :key="experience.employee_id + index">
          <div class="header">
            <img :src="experience.brands[0].logo_url" class="logo" />
            <div class="time">{{ experience.total_shifts }} shift{{ experience.total_shifts > 1 ? 's' : '' }}</div>
          </div>
          <span class="role-name">{{ experience.internal_role?.role || experience.company }}</span>
          <span v-if="experience.internal_role?.role" class="subheading">{{ experience.company }}</span>
          <div class="tags">
            <CompanyRoleBadge v-for="tag in getTags(experience)" :key="tag" :role="tag" :verified="true" />
          </div>
        </div>
      </div>
      <!-- RAISE -->
      <div class="section" v-if="raiseExperience?.length > 0">
        <span class="heading">Raise experience</span>
        <div v-for="func in raiseExperience" :key="func.function" class="role">
          <div class="header">
            <div class="icon-overlay">
              <component
                v-for="(logo, index) in getLogos(func.brands)"
                :key="logo + '-' + index"
                :is="logo !== '' ? 'img' : 'div'"
                :src="logo !== '' ? logo : undefined"
                :style="{
                  right: `${18 * index}px`,
                  zIndex: 3 - index,
                  width: `${60 - index * 8}px`,
                  height: `${60 - index * 8}px`,
                  backgroundColor:
                    logo === '' ? 'var(--palette-color-default-lighten-95)' : 'var(--palette-color-base-white)',
                }"
                class="logo"
              />
            </div>
            <div class="time">{{ func.num_shifts }} shift{{ func.num_shifts > 1 ? 's' : '' }}</div>
          </div>
          <div class="function">
            <span class="role-name">{{ func.function }}</span>
            <UserRatingDisplay :rating="func.rating || 0" small></UserRatingDisplay>
          </div>
          <span class="subheading">{{ func.roles.join(', ') }}</span>
          <span class="subheading mt-2"
            >{{ func.companies.length }} location{{ func.companies.length > 1 ? 's' : '' }}:
            {{ func.companies.join(', ') }}</span
          >
        </div>
      </div>
      <!-- UNVERIFIED -->
      <div class="section" v-if="otherExperience?.length > 0">
        <span class="heading">Other experience</span>
        <div class="experience">
          <div class="experience-indiv" v-for="exp in otherExperience" :key="exp.history_id">
            <div class="timeline">
              <div class="circle"></div>
              <div class="line"></div>
            </div>
            <div class="info">
              <div class="date">
                <span>{{ formatDate(exp) }}</span>
                <span>{{ formatDurationFromExperience(exp) }}</span>
              </div>
              <div class="role-name">{{ exp.role }}</div>
              <div class="subheading">{{ exp.company_name }}</div>
              <div class="description">{{ exp.description }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <app-loading v-else :loading="loading" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import ApiClient from '@/ApiClient';
import UserRatingDisplay from '@/pages/talent-pool/components/UserRatingDisplay.vue';
import { themeImage } from '@/Theme';
import {
  formatWorkerName,
  getAllShiftsWorkedTimeSpan,
  getDaysInFunction,
  getDaysInRole,
  getHoursInFunction,
  getHoursInRole,
} from '@/pages/talent-pool/utils/profile';
import AppLoading from '@/components/AppLoading.vue';
import { differenceInDays, differenceInMonths, differenceInYears, format } from 'date-fns';
import { useRoute } from 'vue-router/composables';
import CompanyRoleBadge from '@/pages/talent-pool/components/CompanyRoleBadge.vue';

const props = defineProps(['claim', 'employeeId', 'vertical']);
const emit = defineEmits(['claim-selected']);

const route = useRoute();
const urlEmployeeId = route.params.employeeId;

const employeeId = props.claim ? props.claim.worker_id : urlEmployeeId || props.employeeId;

const verifiedExperience = ref([]);
const raiseExperience = ref([]);
const otherExperience = ref([]);
const matchingRole = ref({});
const profile = ref('');
const loading = ref(false);

const fetchCandidateDetails = async () => {
  loading.value = true;
  const { data } = await ApiClient.getEmployeeProfile(employeeId);
  profile.value = data;

  if (props.claim) {
    matchingRole.value = props.claim.roles?.find(role => role?.is_function_match);
  }

  loading.value = false;

  verifiedExperience.value = profile.value.wagestream_history?.filter(role => role.total_shifts > 0);
  otherExperience.value = profile.value.unverified_roles.sort((a, b) => {
    const dateA = new Date(`${a.end_year}-${a.end_month}`);
    const dateB = new Date(`${b.end_year}-${b.end_month}`);
    return dateB - dateA;
  });
  raiseExperience.value = profile.value.raise_history?.reduce((formattedList, current) => {
    if (!current.internal_role) {
      return formattedList;
    }

    const existingFunctionIndex = formattedList.findIndex(item => item.function === current.internal_role.function);

    if (existingFunctionIndex === -1) {
      formattedList.push({
        function: current.internal_role.function,
        companies: [current.company],
        rating: current.shift_user_rating,
        roles: [current.internal_role.role],
        num_shifts: 1,
        brands: [current.brand],
      });
    } else {
      formattedList[existingFunctionIndex].num_shifts++;
      // Add companies and roles if they don't already exist
      if (!formattedList[existingFunctionIndex].companies.includes(current.company)) {
        formattedList[existingFunctionIndex].companies.push(current.company);
      }
      if (!formattedList[existingFunctionIndex].roles.includes(current.internal_role.role)) {
        formattedList[existingFunctionIndex].roles.push(current.internal_role.role);
      }
      if (!formattedList[existingFunctionIndex].brands.find(brand => brand.brand === current.brand.brand)) {
        formattedList[existingFunctionIndex].brands.push(current.brand);
      }
      // Update rating to be average of all ratings
      formattedList[existingFunctionIndex].rating =
        (formattedList[existingFunctionIndex].rating + current.shift_user_rating) / 2;
    }

    return formattedList;
  }, []);
};

const hoursInRole = computed(() => {
  return props.claim && getHoursInRole(props.claim.roles);
});

const hoursInFunction = computed(() => {
  return props.claim && getHoursInFunction(props.claim.roles);
});

const daysInRole = computed(() => {
  return props.claim && getDaysInRole(props.claim.roles);
});

const daysInFunction = computed(() => {
  return props.claim && getDaysInFunction(props.claim.roles);
});

const totalShifts = computed(() => {
  const verified_shifts = verifiedExperience.value?.reduce((acc, curr) => acc + curr.total_shifts, 0) || 0;
  const raise_shifts = raiseExperience.value?.reduce((acc, curr) => acc + curr.num_shifts, 0) || 0;
  return verified_shifts + raise_shifts;
});

function formatDuration(startDate, endDate) {
  const years = differenceInYears(endDate, startDate);
  const months = differenceInMonths(endDate, startDate) % 12;
  const days = differenceInDays(endDate, startDate);

  let durationString = '';

  if (years > 0) {
    durationString += `${years} year${years > 1 ? 's' : ''}`;
  }

  if (months > 0) {
    durationString += `${durationString ? ' ' : ''}${months} month${months > 1 ? 's' : ''}`;
  }

  if (days > 0 && durationString === '') {
    durationString += `${days} day${days > 1 ? 's' : ''}`;
  }

  return durationString;
}

function formatDurationFromExperience(role) {
  const startMonthNumber = parseInt(role.start_month.replace('0', ''), 10);
  const endMonthNumber = parseInt(role.end_month.replace('0', ''), 10);

  const startDate = new Date(role.start_year, startMonthNumber - 1);
  const endDate = new Date(role.end_year, endMonthNumber - 1);

  return formatDuration(startDate, endDate);
}

function formatDate(role) {
  const startMonthNumber = parseInt(role.start_month.replace('0', ''), 10);
  const endMonthNumber = parseInt(role.end_month.replace('0', ''), 10);

  const startDate = format(new Date(role.start_year, startMonthNumber - 1), 'MMM yyyy');
  const endDate = format(new Date(role.end_year, endMonthNumber - 1), 'MMM yyyy');

  return `${startDate} - ${endDate}`;
}

const getLogos = brands => {
  const logos = brands?.map(brand => brand.logo_url);
  const paddedLogos = [...logos];
  while (paddedLogos.length < 3) {
    paddedLogos.push('');
  }
  return paddedLogos;
};

const getTags = role => {
  const tags = role?.site_tags?.flatMap(obj => obj && Object.values(obj)).filter(tag => tag !== null);
  return tags;
};

onMounted(() => {
  fetchCandidateDetails();
});
</script>

<style scoped lang="scss">
.profile-container {
  background-color: var(--palette-color-base-background);
  padding: 1rem;
  display: flex;
  justify-content: space-around;
  border-radius: 0 0 8px 8px;
  flex-grow: 1;
  gap: 1rem;
}

.profile-container.vertical {
  flex-direction: column;
  padding: 0;
}

.left {
  background-color: var(--palette-color-base-white);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 35%;
  padding: 1rem;
  gap: 0.75rem;
}

.detail-section {
  display: flex;
  flex-direction: column;
}

.experience-text {
  color: var(--palette-color-default-lighten-30);
  margin-bottom: 0.25rem;
}

.vertical .left {
  width: 100%;
  box-sizing: border-box;
  margin: 0;
}

.worker-name {
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.01em;
  text-align: left;
}

.right {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 1rem;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.heading {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  padding: 0 0.5rem;
}

.role {
  background-color: var(--palette-color-base-white);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  position: relative;
}

.role .header {
  display: flex;
  justify-content: space-between;
}

.role .logo {
  height: 64px;
  width: 64px;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 25rem;
  object-fit: cover;
  margin-bottom: 0.75rem;
  position: relative;
  display: inline-block;
}

.role .time {
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default-lighten-30);
}

.role .tags {
  margin-top: 0.5rem;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.function {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.role-name {
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.01em;
  text-align: left;
}

.subheading {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default-lighten-20);
  margin-bottom: 0.25rem;
}

.experience {
  background-color: var(--palette-color-base-white);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.experience-indiv {
  width: 100%;
  display: flex;
  gap: 0.75rem;
}

.experience-indiv .timeline {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.experience-indiv .circle {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  background-color: var(--palette-color-default-lighten-80);
  margin-top: 3px;
  margin-bottom: 3px;
}

.experience-indiv:last-of-type .line {
  display: none;
}

.experience-indiv .line {
  width: 1px;
  // height: 100%;
  flex-grow: 1;
  background-color: var(--palette-color-default-lighten-80);
  margin: 0 auto;
  // margin-bottom: 3px;
}

.experience-indiv .info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: 1rem;
}

.experience-indiv .description {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
}

.image-header {
  width: 100%;
}

.pick-button {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.companies {
  margin: 0.5rem 0;
}

.light-subheading {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-align: left;
  color: var(--palette-color-default-lighten-30);
}

.small-light {
  color: var(--palette-color-default-lighten-30);
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.01em;
}

.date {
  color: var(--palette-color-default-lighten-30);
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.stat-bold {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.01em;
}

.raise-logo {
  height: 1.5rem;
  margin: 1rem;
}

.rating-container {
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 12px;
  padding: 16px;
  width: 100%;
  margin-top: 1rem;
  align-items: center;
  text-align: center;
  display: flex;
  flex-direction: column;
}

.icon-overlay {
  position: relative;
  display: flex;
  align-items: center;
}

.location {
  margin-top: 0.25rem;
}
</style>
